import { AlertTriangle, Lock, RotateCw, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * The five states every region can be in, as five visibly different things.
 *
 * The product rule this exists to enforce: **a failed read must never be
 * presented as an empty result.** "No conflicts declared" and "the conflicts
 * could not be loaded" mean opposite things — the first invites an organiser to
 * proceed, the second says they are about to decide on information they do not
 * have. Rendered identically, as they were, the screen quietly recommends the
 * wrong action.
 *
 * So each state gets its own shape, not just its own colour:
 *   loading  — skeleton in the shape of the content, no spinner
 *   empty    — neutral, centred, says what would appear here
 *   failed   — bordered, warning icon, names the failure, offers a retry
 *   partial  — content IS shown, with a strip saying what is missing from it
 *   locked   — the viewer lacks the permission; quiet, and not a fault
 *
 * Colour alone carries none of it: every state has an icon or a distinct
 * structure, so the difference survives greyscale and colour blindness.
 */

/**
 * Loading, in the shape of what is coming.
 *
 * Skeletons rather than a spinner: a spinner in the middle of a card tells you
 * only that something is happening, while a skeleton the size of the table keeps
 * the layout from jumping when the rows land.
 */
export function LoadingRows({ rows = 4, className, label }: { rows?: number; className?: string; label?: string }) {
  return (
    <div className={cn('space-y-2', className)} role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="h-9 animate-pulse rounded-md bg-muted"
          // Slight width variation so a stack reads as content rather than as a
          // loading graphic. Deterministic, so it does not flicker on re-render.
          style={{ width: `${100 - (i % 3) * 7}%` }}
        />
      ))}
      {label ? <span className="sr-only">{label}</span> : null}
    </div>
  );
}

/**
 * Nothing here, and that is a normal answer.
 *
 * Deliberately quiet and centred, with no border: it must not compete with the
 * failure state, and an organiser scanning the page should be able to tell the
 * two apart from across a desk.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  /** What would appear here, and what puts it here. Teaches the interface. */
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-10 text-center', className)}>
      {icon ? <div className="text-muted-foreground/60">{icon}</div> : null}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description ? <p className="max-w-[52ch] text-sm text-muted-foreground">{description}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/**
 * The read failed. Structurally unlike the empty state: bordered, tinted,
 * icon-led, and carrying a retry.
 *
 * The copy always names what is NOT KNOWN rather than what went wrong, because
 * the consequence is the actionable part: "it is not known who has scored" tells
 * an organiser not to close the round; "500 Internal Server Error" does not.
 */
export function FailedState({
  title,
  description,
  onRetry,
  retryLabel,
  retrying = false,
  className,
}: {
  title: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
  retrying?: boolean;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-start gap-2 rounded-lg border border-destructive/35 bg-destructive/[0.04] px-4 py-3',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
        </div>
      </div>
      {onRetry ? (
        <Button type="button" size="sm" variant="outline" onClick={onRetry} disabled={retrying} className="ml-6">
          <RotateCw className={cn('size-3.5', retrying && 'animate-spin')} />
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The content loaded, but part of it did not.
 *
 * Rendered ABOVE the content it qualifies rather than instead of it: the rows
 * that arrived are still worth reading, and hiding them because a secondary read
 * failed trades a partial answer for none. The strip says exactly which part is
 * missing, so nobody reads the visible rows as the whole set.
 */
export function PartialStrip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      role="status"
      className={cn(
        'flex items-start gap-2 rounded-lg border border-amber-300/60 bg-status-amber-tint/50 px-3 py-2 text-sm text-[#7C2D12] dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200',
        className,
      )}
    >
      <WifiOff className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

/**
 * The viewer does not hold the permission.
 *
 * Quiet on purpose. A coordinator who can only assign judges should see a screen
 * that looks deliberate, not one that looks broken — so this reads as a fact
 * about their role, never as an error, and it never offers a retry.
 */
export function LockedState({ title, description, className }: { title: string; description?: string; className?: string }) {
  return (
    <div className={cn('flex items-start gap-2 rounded-lg border border-dashed px-4 py-3', className)}>
      <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
      </div>
    </div>
  );
}
